/**
 * UI表示メッセージの定数
 *
 * @description アプリケーション全体で使用されるUIメッセージを一元管理
 * @note バリデーションメッセージは utility/validation.ts で管理
 */

// =============================================================================
// 共通フレーズ（内部参照用）
// =============================================================================

const COMMON_PHRASES = {
  ERROR_OCCURRED: 'エラーが発生しました',
  PERMISSION_DENIED: 'ページを閲覧する権限がありません。',
  URL_INVALID: 'アクセスしたURLは有効ではありません。',
  RETRY_AFTER_WAIT: 'しばらく時間をおいて再度お試しください。',
  RETRY_CHECK_URL_OR_WAIT:
    'URLが正しいかご確認いただくか、しばらく時間をおいて再度お試しください。',
} as const

// =============================================================================
// エラーメッセージ
// =============================================================================

export const ERROR_MESSAGES = {
  // 汎用エラー
  GENERIC: COMMON_PHRASES.ERROR_OCCURRED,
  UNEXPECTED: '予期せぬエラーが発生しました',

  // 読み込みエラー（動的生成）
  LOADING: (entity: string) => `${entity}の読み込み中にエラーが発生しました`,

  // 送信エラー
  SUBMIT: '送信中にエラーが発生しました。もう一度お試しください。',

  // 保存エラー
  SAVE_ERROR: '保存中にエラーが発生しました。もう一度お試しください。',

  // 更新エラー（動的生成）
  UPDATE: (entity: string) => `${entity}の更新に失敗しました。もう一度お試しください。`,

  // 認証エラー
  AUTH: {
    LOGIN_FAILED:
      '入力された内容でログインできませんでした。\n内容をご確認の上、再度お試しください。',
    OAUTH_FAILED:
      'ソーシャルメディア（Google、Twitter）でのログインに失敗しました。\nしばらく経ってから再度お試しください。',
  },

  // リトライメッセージ
  RETRY: '以上をご確認の上、再度お試しください。',
  RETRY_AFTER_WAIT: COMMON_PHRASES.RETRY_AFTER_WAIT,
  RETRY_CHECK_URL_OR_WAIT: COMMON_PHRASES.RETRY_CHECK_URL_OR_WAIT,

  // URL・権限エラー
  URL_INVALID: COMMON_PHRASES.URL_INVALID,
  PERMISSION_DENIED: COMMON_PHRASES.PERMISSION_DENIED,

  // エラーページ（403）
  FORBIDDEN: {
    MESSAGE: COMMON_PHRASES.PERMISSION_DENIED,
    DETAILS: 'しばらく時間をおいてからもう一度アクセスして頂くか、管理者にお問い合わせください。',
  },
  // エラーページ（404）- トークン無効時等
  NOT_FOUND: {
    TITLE: 'ページが見つかりません',
    MESSAGE: COMMON_PHRASES.URL_INVALID,
    DETAILS: COMMON_PHRASES.RETRY_CHECK_URL_OR_WAIT,
  },
  // エラーページ（404）- 一般的な404ページ
  NOT_FOUND_PAGE: {
    TITLE: '指定したページが見つかりません。',
    MESSAGE: 'ページが移動または削除されたか、URLの入力間違いの可能性があります。',
    DETAILS: '再度目的のページをお探しください。',
  },
  // エラーページ（500）
  SERVER: {
    TITLE: 'サーバーエラー',
    MESSAGE:
      'サーバーでエラーが発生しました。\nしばらく時間をおいてから再度アクセスしてください。\n問題が解決しない場合は、管理者にお問い合わせください。',
  },
  // エラーページ（default）
  DEFAULT: {
    TITLE: 'エラー',
    MESSAGE: `${COMMON_PHRASES.ERROR_OCCURRED}。`,
    DETAILS: COMMON_PHRASES.RETRY_AFTER_WAIT,
  },

  // 取得エラー（動的生成）
  FETCH_FAILED: (entity: string) => `${entity}の取得に失敗しました`,

  // 保存エラー（動的生成）
  SAVE_FAILED: (entity: string) => `${entity}の保存に失敗しました`,

  // 登録・アクティベーションエラー
  ACTIVATION_FETCH_FAILED: 'アクティベーションの取得に失敗しました',
  REGISTRATION_FAILED: '登録処理中にエラーが発生しました。しばらくしてから再度お試しください。',

  // パスワード関連エラー
  PASSWORD_SET_FAILED: 'パスワードの設定に失敗しました',
  PASSWORD_RESET_FAILED: 'パスワードリセットに失敗しました',

  // 変更失敗（動的生成）
  CHANGE_FAILED: (entity: string) => `${entity}変更を完了できませんでした。`,

  // 削除失敗（動的生成）
  DELETE_FAILED: (entity: string) => `${entity}を削除できませんでした。`,

  // メールアドレス形式エラー
  EMAIL_FORMAT_INVALID: 'メールアドレスの形式が正しくありません。',

  // エラー時の謝罪メッセージ（全文）
  APOLOGY_WITH_WAIT:
    '大変申し訳ございません。エラーが発生しました。\nエラーの解消まで少々お待ちください。',

  // エラーページ
  PAGE: {
    UNAUTHORIZED: {
      HEADING: '限定公開ページ',
      DESCRIPTIONS: [
        'このページは限定公開されています。',
        'ログインして再度アクセスしてください。',
      ],
      LOGIN_BUTTON: 'ログインする',
    },
    FORBIDDEN: {
      HEADING: 'アクセス権限がありません',
      DESCRIPTIONS: [
        'このページへのアクセスは制限されています。',
        '必要な権限をお持ちでない可能性があります。',
      ],
    },
    NOT_FOUND: {
      HEADING: 'ページが見つかりません',
      DESCRIPTIONS: [
        'あなたがアクセスしたページは削除されたか',
        'URLが変更されているため表示することができません。',
      ],
      SEARCH_LABEL: 'コンテンツを検索',
    },
    SERVER_ERROR: {
      HEADING: 'サーバーエラーが発生しました',
      DESCRIPTIONS: [
        '申し訳ございません。',
        'サーバーで問題が発生しています。',
        'しばらく時間をおいてから、再度お試しください。',
      ],
    },
    OTHER: {
      HEADING: '予期せぬエラーが発生しました',
      DESCRIPTIONS: [
        '大変申し訳ございません。エラーが発生しました。',
        'エラーの解消まで少々お待ちください。',
      ],
    },
  },
} as const

// =============================================================================
// 空状態メッセージ
// =============================================================================

export const EMPTY_STATE_MESSAGES = {
  // 検索結果（動的生成）
  SEARCH_NO_RESULTS: (entity?: string) =>
    entity
      ? `条件に一致する${entity}が見つかりませんでした。`
      : '条件に一致する結果が見つかりませんでした。',

  // リトライを促すメッセージ
  RETRY_CHANGE_KEYWORD: '検索する単語を変えてやり直してください。',
  RETRY_CHANGE_CONDITION: '条件を変更してもう一度やり直してください。',

  // コンテンツ（動的生成）
  NO_CONTENT: (entity: string) => `該当する${entity}がありません。`,
  NO_CONTENT_IN: (parent: string) => `この${parent}にはコンテンツがありません`,
  CONTENT_PREPARING: '現在コンテンツを準備中です。しばらくお待ち下さい。',
  EMPTY: (entity: string) => `${entity}はありません`,
  NOT_FOUND: (entity: string) => `${entity}が見つかりません`,
  NOT_EXIST_OR_DELETED: (entity: string) => `指定された${entity}は存在しないか、削除されました。`,

  // ユーザー関連
  USER: {
    NO_HISTORY: '記事の閲覧履歴がありません',
    NO_DIAGNOSIS_HISTORY: 'まだ診断履歴がありません。',
  },

  // フォロー（動的生成）
  NO_FOLLOWING: (typeName: string) => `フォローしている${typeName}がありません。`,

  // ロールモデル
  ROLE_MODEL_NO_RESULTS:
    '該当するロールモデルが見つかりませんでした。検索条件を変更するか、インタビューの回答を調整して、もう一度お試しください。',

  // 通知（動的生成）
  NO_LATEST: (entity: string) => `最新の${entity}はありません。`,

  // 404
  PAGE_NOT_FOUND: 'ページが見つかりませんでした。',
} as const

// =============================================================================
// 読み込み中メッセージ
// =============================================================================

export const LOADING_MESSAGES = {
  DEFAULT: '読み込み中...',
  VERIFYING: '確認中...',
} as const

// =============================================================================
// アクションメッセージ
// =============================================================================

export const ACTION_MESSAGES = {
  SEARCH_COMPANY: '企業を探す',
  SEARCH_CATEGORY: 'カテゴリを探す',
  SEARCH_ALL_COMPANIES_ROLE_MODEL: 'すべての企業でロールモデルを探す',
} as const

// =============================================================================
// 情報メッセージ
// =============================================================================

export const INFO_MESSAGES = {
  DEFAULT: 'お知らせがあります。',
} as const

// =============================================================================
// 成功メッセージ
// =============================================================================

export const SUCCESS_MESSAGES = {
  DEFAULT: '処理が完了しました。',

  // 設定完了（動的生成）
  SETTING_COMPLETE: (entity: string) => `${entity}設定が完了しました。`,

  // 更新完了（動的生成）
  UPDATED: (entity: string) => `${entity}が正常に更新されました。`,

  // 更新完了＋リダイレクト（動的生成）
  UPDATED_WITH_REDIRECT: (entity: string, seconds: number) =>
    `${entity}が正常に更新されました。${seconds}秒後にホームに移動します。`,

  // 登録完了
  REGISTRATION_COMPLETE: '会員登録が完了しました。',

  // 退会完了
  QUIT_COMPLETE: '退会が完了しました。',

  // メール送信完了
  EMAIL_VERIFICATION_SENT: '変更後のメールアドレスに確認用のメールを送信しました。',
} as const

// =============================================================================
// アノテーションメッセージ
// =============================================================================

export const ANNOTATION_MESSAGES = {
  // メール送信関連
  EMAIL: {
    NOT_RECEIVED_TITLE: 'メールが届かない場合、下記の原因が考えられます。',
    WRONG_ADDRESS: 'メールアドレスが間違って入力されている',
    NOT_REGISTERED: 'ご入力いただいたメールアドレスではタレントブックの登録が見つからない。',
  },

  // グループ関連
  GROUP_JOIN: [
    'グループに参加すると、あなたの会員情報、グループ参加以降のこの企業の記事閲覧履歴が企業に開示されます。',
    '会員情報は正確なものをご記載ください。招待した人である確認が取れない場合、企業はグループから脱退させることがあります。',
    'グループに参加すると、この企業を自動的に「フォロー」します。',
  ],
  GROUP_JOIN_INFO: `タレントブック・ストーリーは、\n"この人みたいになりたい"からはじまるロールモデル就活・転職サイトです。\n\n本グループでは、グループ限定の社員ストーリーやコンテンツを通じて企業理解やキャリア検討を深めていただけます。\n\n※ 登録・利用は無料です`,
  LEAVE_MODAL: '退会処理は即座に実行され、取り消しできません',

  // BANされたユーザー
  BANNED_USER: 'このグループにはご参加いただけません',

  // アノテーションタイトル
  ICON_TITLE: {
    GROUP_JOIN: '注意事項',
    GROUP_JOIN_REQUIRED: '参加にはタレントブックへの登録が必要です。',
    LEAVE_MODAL: '重要な注意',
  },

  // URL関連
  URL: {
    CHECK_URL: 'URLが間違っていないかご確認ください。',
    EXPIRED: (pageName?: string) =>
      pageName
        ? `URLが間違っていない場合は有効期限が切れています。その場合は${pageName}をやり直してください。`
        : 'URLが間違っていない場合は有効期限が切れています。その場合はお手数ですが再度やり直してください。',
  },
} as const

// =============================================================================
// ボタン状態メッセージ
// =============================================================================

export const BUTTON_MESSAGES = {
  // --- ローディング状態 ---
  SUBMITTING: '送信中...',
  SAVING: '保存中...',
  PROCESSING: '処理中...',
  DELETING: '削除中...',
  UPDATING: '更新中...',
  REGISTERING: '登録中...',
  FOLLOWING: 'フォロー中...',
  ADDING: '追加中...',

  // --- 基本アクション ---
  SUBMIT: '送信',
  SAVE: '保存する',
  SAVE_CHANGES: '変更を保存',
  UPDATE: '更新する',
  REGISTER: '登録',
  CANCEL: 'キャンセル',
  CLOSE: '閉じる',
  RETRY: '再試行する',

  // --- 確認・削除 ---
  CONFIRM: '確認画面へ',
  CONFIRM_ENTITY: (entity: string) => `${entity}を確認`,
  DELETE_ACCOUNT: 'アカウント削除',
  DELETE_ACCOUNT_CONFIRM: '注意事項を理解した上でアカウント削除',

  // --- ナビゲーション ---
  BACK: '戻る',
  NEXT: '次へ',
  BACK_TO_TOP: 'タレントブック・ストーリー TOPページに戻る',
  PREV_ARTICLE: '前の記事',
  NEXT_ARTICLE: '次の記事',

  // --- 表示切替 ---
  VIEW_DETAILS: '詳細を表示',
  READ_MORE: '続きを読む',
  SHOW_LESS: '一部を表示',
  CLOSE_MENU: 'メニューを閉じる',

  // --- 一括操作 ---
  REMOVE_ALL: '全ての選択を削除',
  BULK_FOLLOW: 'まとめてフォロー',
  BULK_ADD: 'まとめて追加',
  BULK_FOLLOW_COMPANIES: '企業を一括フォロー',
  BULK_ADD_TO_BOOKSHELF: 'マイ本棚に一括追加',

  // --- 本棚操作 ---
  ADD_TO_BOOKSHELF: 'マイ本棚に追加',
  REMOVE_FROM_BOOKSHELF: 'マイ本棚から削除',

  // --- 完了アクション ---
  COMPLETE_TO_MYPAGE: '完了してマイページへ',
  COMPLETE_AND_CLOSE: '完了して閉じる',
} as const

// =============================================================================
// フォームメッセージ
// =============================================================================

export const FORM_MESSAGES = {
  CONFIRM_INPUT: '入力内容をご確認ください',
  PASSWORD_CONFIRM_HINT: '確認のためもう1度入力してください。',
  NEW_PASSWORD_PROMPT: '新しいパスワードを入力してください。',
} as const

// =============================================================================
// ラベルメッセージ
// =============================================================================

export const LABEL_MESSAGES = {
  // 一括操作関連
  COMPANIES_TO_FOLLOW: '今回フォローする企業',
  ARTICLES_TO_ADD: '今回追加する記事',
  ALREADY_FOLLOWED_COMPANIES: '既にフォロー済みの企業',
  ALREADY_ADDED_ARTICLES: '既に追加済みの記事',
} as const
